import {Avatar, Button, Card, Separator} from 'platform/components';
import {Box, HStack, Show, Text, ThemeColorPath, VStack} from 'platform/foundation';
import {formatPhoneNumber, useDateTimeFormatter} from 'platform/locale';
import {match} from 'ts-pattern';

import {isNotNil} from 'ramda';
import {isNotNilOrEmpty} from 'ramda-adjunct';

import {CustomerMessageCallResponseBody, CustomerMessageResponseBody} from '@dms/api/core';
import {EntityResourceIds} from '@dms/api/shared';
import i18n from '@dms/i18n';
import {telephonyRoutes} from '@dms/routes';

import {
  buildArray,
  composePath,
  DOT_CHARACTER,
  parseDate,
  suffixTestId,
  TestIdProps,
  useBoolean,
  useNavigate,
} from 'shared/web';

import {TelephonyCallPairingResource} from '../../../types/TelephonyCallPairingResource';
import {formatCallDuration} from '../../../utils/formatCallDuration';
import {getTelephonyCallOutcomeLabel} from '../../../utils/getTelephonyCallOutcomeLabel';
import {isMissedTelephonyCallOutcome} from '../../../utils/isMissedTelephonyCallOutcome';
import {MessageContextLink} from '../types/MessageContextLink';
import {ThreadCallNoteEditor} from './ThreadCallNoteEditor';
import {ThreadContextLinks} from './ThreadContextLinks';

export interface ThreadCallPairing {
  resourceId: TelephonyCallPairingResource;
  recordId: string;
  /** The call whose pairing is being saved; only its button shows loading. */
  pendingCallId: string | null;
  onPair: (callId: string) => void;
  onUnpair: (callId: string) => void;
}

interface ThreadCallCardProps extends TestIdProps {
  message: CustomerMessageResponseBody;
  call: CustomerMessageCallResponseBody;
  contextLinks: MessageContextLink[];
  /** The record the thread is on, when the call can be paired with it from here. */
  pairing?: ThreadCallPairing;
  /** The viewer may write the call note; otherwise an existing note only shows. */
  canEditNote?: boolean;
  /** The viewer may open the call's detail page. */
  canOpenCall?: boolean;
}

/** Same width rule as the e-mail card, so calls and e-mails line up in the thread. */
const CARD_MIN_WIDTH = '60%';

/** A connected call reads as its direction and length; any other outcome as its sentence. */
const getTitle = (call: CustomerMessageCallResponseBody, isInbound: boolean) =>
  call.outcome === 'connected'
    ? [
        isInbound
          ? i18n.t('entity.activityPanel.activity.inboundCall')
          : i18n.t('entity.activityPanel.activity.outboundCall'),
        formatCallDuration(call.durationSeconds ?? 0),
      ].join(` ${DOT_CHARACTER} `)
    : getTelephonyCallOutcomeLabel(call.outcome);

const getPairLabel = (resourceId: TelephonyCallPairingResource) =>
  match(resourceId)
    .with(EntityResourceIds.interest, () => i18n.t('page.telephony.pairing.pairWithInterest'))
    .with(EntityResourceIds.businessCase, () =>
      i18n.t('page.telephony.pairing.pairWithBusinessCase')
    )
    .with(EntityResourceIds.serviceCase, () => i18n.t('page.telephony.pairing.pairWithServiceCase'))
    .with(EntityResourceIds.workshopInterest, () =>
      i18n.t('page.telephony.pairing.pairWithWorkshopInterest')
    )
    .exhaustive();

/**
 * A call in the customer thread, drawn like the e-mail card so the thread reads as one design:
 * colour by direction (blue in, green out, red missed), a phone instead of the envelope, inbound
 * on the left, outbound on the right. The header carries pairing with the host record; the body
 * holds the note and is left out while there is nothing to show.
 */
export function ThreadCallCard(props: ThreadCallCardProps) {
  const formatDateTime = useDateTimeFormatter();
  const navigate = useNavigate();
  const [isEditingNote, startEditingNote, stopEditingNote] = useBoolean(false);

  const isInbound = props.message.direction === 'INCOMING';
  const isMissed = isMissedTelephonyCallOutcome(props.call.outcome);
  const colorStripe = match({isMissed, isInbound})
    .returnType<'red' | 'blue' | 'green'>()
    .with({isMissed: true}, () => 'red')
    .with({isInbound: true}, () => 'blue')
    .otherwise(() => 'green');
  const avatarColor = match(colorStripe)
    .returnType<ThemeColorPath>()
    .with('red', () => 'palettes.red.60.100')
    .with('blue', () => 'palettes.blue.60.100')
    .with('green', () => 'palettes.green.60.100')
    .exhaustive();

  const linkedRecords = [
    {recordId: props.message.recordId, resourceId: props.message.resourceId},
    ...props.message.secondaryRecords,
  ];
  const isPairedHere =
    isNotNil(props.pairing) &&
    linkedRecords.some(
      (record) =>
        record.recordId === props.pairing?.recordId &&
        record.resourceId === props.pairing?.resourceId
    );

  const details = buildArray<string>()
    .when(isNotNil(props.call.user), () =>
      isInbound
        ? i18n.t('page.telephony.indicator.answeredBy', {name: props.call.user?.name})
        : (props.call.user?.name ?? '')
    )
    .add(
      `${props.call.line.name} ${formatPhoneNumber(props.call.line.phoneNumber) ?? props.call.line.phoneNumber}`
    )
    .join(` ${DOT_CHARACTER} `);

  const note = isNotNilOrEmpty(props.call.note) ? props.call.note : null;
  const hasBody = isNotNil(note) || isEditingNote;

  return (
    <HStack width="100%" justify={isInbound ? 'flex-start' : 'flex-end'}>
      <Box minWidth={CARD_MIN_WIDTH} data-testid={suffixTestId('threadCallCard', props)}>
        <Card colorStripe={colorStripe}>
          <VStack spacing={3}>
            <HStack width="100%" spacing={3} align="center" justify="space-between" wrap>
              <HStack spacing={2} align="center">
                <Avatar
                  icon="communication/call"
                  color={avatarColor}
                  iconColor="general.white"
                  size="small"
                />
                <VStack>
                  <Text
                    size="small"
                    alternative
                    color={isMissed ? 'danger' : undefined}
                    data-testid={suffixTestId('threadCallCard-title', props)}
                  >
                    {getTitle(props.call, isInbound)}
                  </Text>
                  <Text size="xSmall" color="tertiary">
                    {details}
                  </Text>
                </VStack>
              </HStack>
              <HStack spacing={2} align="center" wrap>
                <Text size="xSmall" color="tertiary" noWrap>
                  {formatDateTime('dateTimeShort', parseDate(props.message.createdAt))}
                </Text>
                <Show when={isNotNilOrEmpty(props.contextLinks)}>
                  <ThreadContextLinks links={props.contextLinks} />
                </Show>
                <Show when={props.pairing}>
                  {(pairing) =>
                    isPairedHere ? (
                      <HStack spacing={1} align="center">
                        <Text size="small" color="success">
                          {i18n.t('page.telephony.pairing.paired')}
                        </Text>
                        <Button
                          variant="link"
                          size="small"
                          title={i18n.t('page.telephony.pairing.undo')}
                          isLoading={pairing.pendingCallId === props.call.id}
                          onClick={() => pairing.onUnpair(props.call.id)}
                          data-testid={suffixTestId('threadCallCard-unpair', props)}
                        />
                      </HStack>
                    ) : (
                      <Button
                        variant="secondary"
                        size="small"
                        leftIcon="content/link"
                        title={getPairLabel(pairing.resourceId)}
                        isLoading={pairing.pendingCallId === props.call.id}
                        onClick={() => pairing.onPair(props.call.id)}
                        data-testid={suffixTestId('threadCallCard-pair', props)}
                      />
                    )
                  }
                </Show>
                <Show when={props.canEditNote && !hasBody}>
                  <Button
                    variant="link"
                    size="small"
                    leftIcon="content/add"
                    title={i18n.t('general.actions.addNote')}
                    onClick={startEditingNote}
                    data-testid={suffixTestId('threadCallCard-callNote-add', props)}
                  />
                </Show>
                <Show when={props.canOpenCall}>
                  <Button
                    variant="link"
                    size="small"
                    leftIcon="action/open_in_new"
                    title={i18n.t('general.labels.detail')}
                    onClick={() =>
                      navigate(
                        composePath(telephonyRoutes.callDetail, {params: {id: props.call.id}})
                      )
                    }
                    data-testid={suffixTestId('threadCallCard-detail', props)}
                  />
                </Show>
              </HStack>
            </HStack>

            <Show when={hasBody}>
              <Separator spacing={0} />
              {isEditingNote ? (
                <ThreadCallNoteEditor
                  callId={props.call.id}
                  note={note}
                  onClose={stopEditingNote}
                  data-testid={suffixTestId('threadCallCard', props)}
                />
              ) : (
                <VStack spacing={1}>
                  <HStack spacing={2} align="center" justify="space-between">
                    <Text size="xSmall" color="secondary" alternative>
                      {i18n.t('general.labels.note')}
                    </Text>
                    <Show when={props.canEditNote}>
                      <Button
                        variant="link"
                        size="small"
                        leftIcon="image/edit"
                        title={i18n.t('general.actions.edit')}
                        onClick={startEditingNote}
                        data-testid={suffixTestId('threadCallCard-callNote-edit', props)}
                      />
                    </Show>
                  </HStack>
                  <Text
                    size="small"
                    preWrap
                    overflowWrap="anywhere"
                    data-testid={suffixTestId('threadCallCard-callNote-text', props)}
                  >
                    {note}
                  </Text>
                </VStack>
              )}
            </Show>
          </VStack>
        </Card>
      </Box>
    </HStack>
  );
}
